import React from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, ShieldCheck, FileText, HelpCircle, X, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguageUtils } from '@/context/language-context';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import PricingPlanCard from '@/components/pricing/PricingPlanCard';
import PricingInformationBlock from '@/components/pricing/PricingInformationBlock';
import PricingComparisonTable from '@/components/pricing/PricingComparisonTable';
import PricingFAQ from '@/components/pricing/PricingFAQ';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from 'react-i18next';

const staticFeaturesData = [
  {
    key: 'no_ads',
    teaser: true,
    regular: true,
    vip: true,
  },
  {
    key: 'access_teaser',
    teaser: true,
    regular: true,
    vip: true,
  },
  {
    key: 'basic_messaging',
    teaser: true,
    regular: true,
    vip: true,
  },
  {
    key: 'view_contacts',
    teaser: true,
    regular: true,
    vip: true,
  },
  {
    key: 'client_verification',
    teaser: false,
    regular: true,
    vip: true,
  },
  {
    key: 'full_access',
    teaser: false,
    regular: true,
    vip: true,
  },
  {
    key: 'leave_reviews',
    teaser: false,
    regular: true,
    vip: true,
  },
  {
    key: 'priority_support',
    teaser: false,
    regular: true,
    vip: true,
  },
  {
    key: 'vip_badge',
    teaser: false,
    regular: false,
    vip: true,
  },
  {
    key: 'early_access',
    teaser: false,
    regular: false,
    vip: true,
  },
  {
    key: 'vip_escorts',
    teaser: false,
    regular: false,
    vip: true,
  },
  {
    key: 'discounted_rates',
    teaser: false,
    regular: false,
    vip: true,
  }
];

const staticFaqData = [
  { id: 'faq1', questionKey: 'pricing.faq.how_subscriptions', answerKey: 'pricing.faq.how_subscriptions_answer' },
  { id: 'faq2', questionKey: 'pricing.faq.payment_secure', answerKey: 'pricing.faq.payment_secure_answer' },
  { id: 'faq3', questionKey: 'pricing.faq.refund', answerKey: 'pricing.faq.refund_answer' },
  { id: 'faq4', questionKey: 'pricing.faq.verification', answerKey: 'pricing.faq.verification_answer' },
];

const planTitleKeysData = {
  teaser: 'subscription.teaser.title',
  regular: 'subscription.regular.title',
  vip: 'subscription.vip.title',
};

const Pricing = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['pricing', 'subscription', 'common']);
  const { getUrlWithLanguage } = useLanguageUtils();

  const features = staticFeaturesData.map(f => ({
    ...f,
    name: t(`pricing.${f.key}`),
    description: t(`pricing.${f.key}_description`)
  }));

  const faqItems = staticFaqData;
  const planTitleKeys = planTitleKeysData;

  return (
    <Layout>
      <div className="py-16 px-4 bg-muted/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold mb-4">{t('pricing.title')}</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <PricingPlanCard
              planKey="teaser"
              title={t('subscription.teaser.title')}
              price={t('subscription.teaser.price')}
              features={features.filter(f => f.teaser).slice(0, 4)}
              badgeText={t('pricing.basic')}
              badgeVariant="pink"
              ctaText={t('pricing.get_started')}
              ctaVariant="outline"
              onCtaClick={() => navigate(getUrlWithLanguage('/subscription'))}
            />

            <PricingPlanCard
              planKey="regular"
              title={t('subscription.regular.title')}
              price={t('subscription.regular.price')}
              features={features.filter(f => f.regular).slice(4, 8)}
              isPopular={true}
              badgeText={t('pricing.popular')}
              badgeVariant="pink"
              ctaText={t('pricing.subscribe_now')}
              ctaClassName="bg-pink-gradient hover:opacity-90"
              cardClassName="border-primary shadow-lg scale-105 z-10"
              onCtaClick={() => navigate(getUrlWithLanguage('/subscription'))}
            />

            <PricingPlanCard
              planKey="vip"
              title={t('subscription.vip.title')}
              price={t('subscription.vip.price')}
              features={features.filter(f => f.vip).slice(8, 12)}
              badgeText={t('pricing.elite')}
              badgeVariant="gold"
              ctaText={t('pricing.go_vip')}
              ctaClassName="bg-gold-DEFAULT text-black hover:bg-gold-dark"
              onCtaClick={() => navigate(getUrlWithLanguage('/subscription'))}
            />
          </div>
          
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
            <PricingInformationBlock
              IconComponent={ShieldCheck}
              iconColorClass="text-pink-500"
              title={t('pricing.privacy_title')}
              description={t('pricing.privacy_description')}
              buttonText={t('pricing.learn_more')}
              cardClassName="bg-gradient-to-br from-pink-50 to-white"
              onButtonClick={() => navigate(getUrlWithLanguage('/security'))}
            />
            <PricingInformationBlock
              IconComponent={FileText}
              iconColorClass="text-blue-500"
              title={t('pricing.content_title')}
              description={t('pricing.content_description')}
              buttonText={t('pricing.see_examples')}
              cardClassName="bg-gradient-to-br from-blue-50 to-white"
              onButtonClick={() => navigate(getUrlWithLanguage('/how-it-works'))}
            />
            <PricingInformationBlock
              IconComponent={HelpCircle}
              iconColorClass="text-purple-500"
              title={t('pricing.support_title')}
              description={t('pricing.support_description')}
              buttonText={t('pricing.contact_support')}
              cardClassName="bg-gradient-to-br from-purple-50 to-white"
              onButtonClick={() => navigate(getUrlWithLanguage('/support'))}
            />
          </div>
          
          <PricingComparisonTable 
            features={features} 
            planTitleKeys={planTitleKeys} 
          />
          
          <PricingFAQ faqItems={faqItems} />
        </div>
      </div>
    </Layout>
  );
};

export default Pricing;
